import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';

export const DEFAULT_BRANDING = {
  primary_color: '#00F5FF', secondary_color: '#A855F7', accent_color: '#CCFF00',
  bg_color: '#050612', text_color: '#E0F7FF', bg_overlay: 0.58,
};

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

export const brandingVars = (b) => ({
  '--brand-primary': b.primary_color,
  '--brand-primary-rgb': hexToRgb(b.primary_color),
  '--brand-secondary': b.secondary_color,
  '--brand-secondary-rgb': hexToRgb(b.secondary_color),
  '--brand-accent': b.accent_color,
  '--brand-bg': b.bg_color,
  '--brand-bg-rgb': hexToRgb(b.bg_color),
  '--brand-text': b.text_color,
  '--brand-overlay': b.bg_overlay,
    '--brand-accent': b.accent_color,
  '--brand-accent-rgb': hexToRgb(b.accent_color),
  '--brand-text': b.text_color,
  '--brand-text-rgb': hexToRgb(b.text_color),
  
});

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING, logoUrl: null, logoTopUrl: null, logoBottomUrl: null,
  bgImageUrl: null, setOrgOverride: () => {},
});

const THEME_VARS = [
  '--brand-primary','--brand-primary-rgb','--brand-secondary','--brand-secondary-rgb',
  '--brand-accent','--brand-accent-rgb','--brand-bg','--brand-bg-rgb',
  '--brand-text','--brand-text-rgb','--brand-overlay','--brand-bg-image',
  '--org-accent','--org-accent2','--org-accent3','--org-scan','--org-bg',
];

export const ThemeProvider = ({ children }) => {
  const { user, refreshUser } = useAuth();

  const isCompanyUser = !!user?.id && user.role !== 'superadmin';
  const org = isCompanyUser ? (user.organization || null) : null;

  useEffect(() => {
    if (!isCompanyUser || !refreshUser) return;
    refreshUser();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshUser(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isCompanyUser]);

   const assetUrl = (u) => {
  if (!u) return null;
  const i = u.indexOf('/static/branding/');
  const path = i >= 0 ? u.slice(i) : u;
  return /^https?:/.test(path) ? path : `${process.env.REACT_APP_API_URL || ''}${path}`;
};

const value = useMemo(() => ({
  branding: { ...DEFAULT_BRANDING, ...(org?.branding || {}) },
  logoUrl: assetUrl(org?.logo_url),
  logoTopUrl: assetUrl(org?.logo_top_url || org?.logo_url),
  logoBottomUrl: assetUrl(org?.logo_bottom_url),
  bgImageUrl: assetUrl(org?.bg_image_url),
  setOrgOverride: () => {},
}), [org]);

  useEffect(() => {
    const root = document.documentElement;
    if (!org) {
      THEME_VARS.forEach(v => root.style.removeProperty(v));
      return;
    }
    const vars = {
  ...brandingVars(value.branding),
  '--org-accent':  value.branding.primary_color,
  '--org-accent2': value.branding.secondary_color,
  '--org-accent3': value.branding.accent_color,
  '--org-scan':    `rgb(${hexToRgb(value.branding.primary_color)} / 0.4)`,
  '--org-bg':      value.branding.bg_color,
};
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    if (value.bgImageUrl) root.style.setProperty('--brand-bg-image', `url('${value.bgImageUrl}')`);
    else root.style.removeProperty('--brand-bg-image');
  }, [org, value]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
export const useOrgBranding = () => useContext(BrandingContext);
export const useOrgLogo = () => useContext(BrandingContext).logoUrl;
export const useOrgLogos = () => {
  const { logoTopUrl, logoBottomUrl } = useContext(BrandingContext);
  return { logoTopUrl, logoBottomUrl };
};